// Listing modes for Time Off (Synergy P1) — My requests / To approve / Team.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. A MINOR
// module: lists only, no Dashboard. Each mode is one server scope
// (app/time_off/service.py list_leave):
//
//   <base>/mine     → scope=mine        the caller's own requests
//   <base>/approve  → scope=to_approve  waiting for the caller's decision
//   <base>/team     → scope=team        every request (leave managers only)
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

import type { TimeOffScope } from "./types";

export const MODE_LABELS: { [segment: string]: string } = {
  mine: "My requests",
  approve: "To approve",
  team: "Team",
};

/** BP-85: the grey line under each label, read off what the mode lists. */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  mine: "Your time off",
  approve: "Waiting for your decision",
  team: "All staff requests",
};

/** Mode segment → the server's list scope. */
export const MODE_SCOPES: { [segment: string]: TimeOffScope } = {
  mine: "mine",
  approve: "to_approve",
  team: "team",
};

/** Modes only a leave manager uses; the layout hides them from everyone else. */
export const MANAGER_MODES: ReadonlySet<string> = new Set(["approve", "team"]);

export const DEFAULT_MODE_SEGMENT = "mine";

export const MODE_STORAGE_KEY = "time_off.viewMode";

export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const parts = pathname.split("/").filter(Boolean);
  const modeCandidate = parts[parts.length - 1] || "";
  const onMode = isModeSegment(modeCandidate);
  const currentMode = onMode ? modeCandidate : readRememberedModeSegment();
  const basePath = onMode ? "/" + parts.slice(0, -1).join("/") : "/" + parts.join("/");
  return { basePath, currentMode, onMode };
}
